import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Link as BaseLink,
  Box,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_GlobalFilterTextField as SearchField,
  MRT_TableInstance,
  MRT_Updater,
  MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table';
import { Link } from 'react-router-dom';
import {
  FormattedMessage,
  IntlShape,
  defineMessages,
  useIntl,
} from 'react-intl';

import { GET_AMCR_FIND_PREVIEWS_QUERY } from '../../../gql';
import {
  useMaterialReactTableLocalization,
  useVocabulary,
} from '../../../i18n';
import {
  AmcrFindPreviewResult,
  getAmcrProblemMessage,
} from '../../../components/AmcrPasDialog';
import {
  Button,
  IconButton,
  MaterialIndicator,
  Pill,
} from '../../../components/design-system';
import {
  FindListFilters,
  FindResultsState,
} from '../../../components/FindResults';
import { searchFieldStyles } from '../../../components/FindResults/searchFieldStyles';
import {
  PasFind,
  getMissingPasFields,
  getPasFindTitle,
  getPasSituationSummary,
  pasFieldLabels,
} from '../../../components/FindPasEditDialog';
import { PasFindThumbnail } from './PasFindThumbnail';
import { PasStage } from './pasStages';

interface PasFindRow {
  id: string;
  find: PasFind;
  title: string;
  situation: string;
}

interface PasFindTableProps {
  rows: PasFind[];
  loading: boolean;
  state: FindResultsState;
  stage: PasStage;
  pasEnabled: boolean;
  onEdit: (find: PasFind) => void;
  onCreate: (findId: string) => void;
  onAddPhotos: (find: PasFind) => void;
  onSubmit: (find: PasFind) => void;
}

const PAGE_SIZE = 50;
const SITE_PRESET_OMITTED_KEYS = ['sites'] as const;

const tableMessages = defineMessages({
  photo: { id: 'sitePas.column.photo', defaultMessage: 'Photo' },
  id: { id: 'sitePas.column.id', defaultMessage: 'ID' },
  pasId: { id: 'sitePas.column.pasId', defaultMessage: 'PAS ID' },
  find: { id: 'sitePas.column.find', defaultMessage: 'Find' },
  situation: { id: 'find.section.situation', defaultMessage: 'Situation' },
  status: { id: 'sitePas.column.status', defaultMessage: 'Status' },
  actions: { id: 'general.actions', defaultMessage: 'Actions' },
  missingValues: {
    id: 'findPasEdit.missingValues',
    defaultMessage: 'Missing for PAS: {fields}',
  },
  edit: { id: 'sitePas.action.edit', defaultMessage: 'Quick edit' },
  photos: {
    id: 'sitePas.status.photos',
    defaultMessage: '{count, plural, one {# photo} other {# photos}}',
  },
});

const gridStyles = {
  marginBottom: 6,
  display: 'grid',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  backgroundImage: 'none',
  overflow: 'hidden',
  '& .MuiTableCell-root': {
    borderColor: 'divider',
  },
};
const toolbarStyles = {
  display: 'flex',
  gap: 1.5,
  px: 2,
  py: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const countStyles = {
  ml: 'auto',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};
const statusCellStyles = {
  alignItems: 'center',
  justifyContent: 'flex-end',
};
const warningIconStyles = { display: 'flex', color: 'warning.main' };
const problemStyles = { gap: 0.5 };
const titleCellStyles = { alignItems: 'center', minWidth: 0 };
const rightAlignedCellProps = {
  align: 'right' as const,
  sx: { verticalAlign: 'middle' },
};
const initialState = { density: 'compact' as const, showGlobalFilter: true };
const paginationProps = { showRowsPerPage: false };
const searchTextFieldProps = { size: 'small' as const, sx: searchFieldStyles };
const tablePaperProps = { sx: gridStyles };
const tableBodyCellProps = { sx: { verticalAlign: 'middle' } };

const getSearchValues = ({ find, title, situation }: PasFindRow) => [
  find.id,
  find.evId,
  find.amcrId,
  find.description,
  find.localization,
  title,
  situation,
];

const matchesSearch = (row: PasFindRow, needle: string) =>
  getSearchValues(row).some((value) =>
    value?.toLocaleLowerCase().includes(needle)
  );

/** Missing values first, then export problems the server reported. */
const getPasProblems = (
  intl: IntlShape,
  find: PasFind,
  preview: AmcrFindPreviewResult | undefined
) => {
  const missingFields = getMissingPasFields(find);
  const coveredPaths = missingFields.map((key) => `find.${key}`);
  const serverProblems = (preview?.errors ?? [])
    .filter(({ path }) => !coveredPaths.includes(path))
    .map((detail) => getAmcrProblemMessage(intl, detail));

  if (missingFields.length === 0) {
    return serverProblems;
  }

  const fields = missingFields
    .map((key) => intl.formatMessage(pasFieldLabels[key]))
    .join(', ');

  return [
    intl.formatMessage(tableMessages.missingValues, { fields }),
    ...serverProblems,
  ];
};

interface PasFindStatusCellProps {
  find: PasFind;
  preview: AmcrFindPreviewResult | undefined;
  onEdit: (find: PasFind) => void;
}

const PasFindStatusCell = React.memo(
  ({ find, preview, onEdit }: PasFindStatusCellProps) => {
    const intl = useIntl();
    const problems = getPasProblems(intl, find, preview);

    const handleEdit = () => {
      onEdit(find);
    };

    const problemText =
      problems.length === 1 ? (
        problems[0]
      ) : (
        <Stack sx={problemStyles}>
          {problems.map((problem) => (
            <Typography key={problem} variant="inherit">
              {problem}
            </Typography>
          ))}
        </Stack>
      );

    return (
      <Stack direction="row" gap={0.5} sx={statusCellStyles}>
        {problems.length > 0 && (
          <Tooltip title={problemText}>
            <Box component="span" sx={warningIconStyles}>
              <WarningAmberRoundedIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}
        <IconButton
          tone="neutral"
          variant="ghost"
          density="compact"
          aria-label={intl.formatMessage(tableMessages.edit)}
          onClick={handleEdit}
        >
          <EditOutlinedIcon />
        </IconButton>
      </Stack>
    );
  }
);

PasFindStatusCell.displayName = 'PasFindStatusCell';

const getPasPhotoCount = (find: PasFind) => find.amcrData?.files?.length ?? 0;

const PasPhotoStatusCell = React.memo(({ find }: { find: PasFind }) => {
  const intl = useIntl();
  const count = getPasPhotoCount(find);

  if (count === 0) {
    return null;
  }

  return (
    <Pill tone="neutral">
      {intl.formatMessage(tableMessages.photos, { count })}
    </Pill>
  );
});

PasPhotoStatusCell.displayName = 'PasPhotoStatusCell';

const PasFindTitle = React.memo(({ row }: { row: PasFindRow }) => (
  <Stack direction="row" gap={1} sx={titleCellStyles}>
    <MaterialIndicator material={row.find.material || 'unknown'} />
    <span>{row.title || '—'}</span>
  </Stack>
));

PasFindTitle.displayName = 'PasFindTitle';

interface PasCreateButtonProps {
  findId: string;
  onCreate: (findId: string) => void;
}

const PasCreateButton = React.memo(
  ({ findId, onCreate }: PasCreateButtonProps) => {
    const handleCreate = () => {
      onCreate(findId);
    };

    return (
      <Button density="compact" onClick={handleCreate}>
        <FormattedMessage id="amcrPas.create.submit" defaultMessage="Create" />
      </Button>
    );
  }
);

PasCreateButton.displayName = 'PasCreateButton';

interface PasPhotoActionsProps {
  find: PasFind;
  canSubmit: boolean;
  onAddPhotos: (find: PasFind) => void;
  onSubmit: (find: PasFind) => void;
}

const PasPhotoActions = React.memo(
  ({ find, canSubmit, onAddPhotos, onSubmit }: PasPhotoActionsProps) => {
    const handleAddPhotos = () => {
      onAddPhotos(find);
    };

    const handleSubmit = () => {
      onSubmit(find);
    };

    return (
      <Stack direction="row" gap={0.75} justifyContent="flex-end">
        <Button density="compact" onClick={handleAddPhotos}>
          <FormattedMessage
            id="sitePas.action.addPhotos"
            defaultMessage="Add photos"
          />
        </Button>
        {canSubmit && (
          <Button density="compact" onClick={handleSubmit}>
            <FormattedMessage
              id="sitePas.action.submit"
              defaultMessage="Submit"
            />
          </Button>
        )}
      </Stack>
    );
  }
);

PasPhotoActions.displayName = 'PasPhotoActions';

/**
 * Site finds in one PAS stage. Search runs here rather than in the table so
 * the PAS check covers exactly the finds on the visible page.
 */
const PasFindTable = React.memo(
  ({
    rows,
    loading,
    state,
    stage,
    pasEnabled,
    onEdit,
    onCreate,
    onAddPhotos,
    onSubmit,
  }: PasFindTableProps) => {
    const intl = useIntl();
    const vocabulary = useVocabulary();
    const localization = useMaterialReactTableLocalization();
    const { search, setSearch, page, setPage } = state;

    const tableRows = useMemo<PasFindRow[]>(
      () =>
        rows.map((find) => ({
          id: find.id,
          find,
          title: getPasFindTitle(vocabulary, find),
          situation: getPasSituationSummary(intl, find),
        })),
      [intl, rows, vocabulary]
    );
    const filteredRows = useMemo(() => {
      const needle = search.trim().toLocaleLowerCase();

      return needle
        ? tableRows.filter((row) => matchesSearch(row, needle))
        : tableRows;
    }, [search, tableRows]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const pageIndex = Math.min(page, pageCount) - 1;
    const pagination = useMemo(() => ({ pageIndex, pageSize: PAGE_SIZE }), [
      pageIndex,
    ]);
    const pageFindIds = useMemo(
      () =>
        filteredRows
          .slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE)
          .map(({ id }) => id),
      [filteredRows, pageIndex]
    );

    const previewsQuery = useQuery(GET_AMCR_FIND_PREVIEWS_QUERY, {
      variables: { findIds: pageFindIds },
      fetchPolicy: 'cache-and-network',
      skip: stage !== 'unprocessed' || pageFindIds.length === 0,
    });
    const previewsById = useMemo(() => {
      const previews: AmcrFindPreviewResult[] =
        previewsQuery.data?.getAmcrFindPreviews ?? [];

      return new Map(previews.map((preview) => [preview.findId, preview]));
    }, [previewsQuery.data]);

    const showUnprocessedStatus = stage === 'unprocessed';
    const showPhotoStatus = stage === 'drafted' || stage === 'withPhoto';
    const showCreateAction = pasEnabled && stage === 'unprocessed';
    const showPhotoActions =
      pasEnabled && (stage === 'drafted' || stage === 'withPhoto');

    const columns = useMemo<MRT_ColumnDef<PasFindRow>[]>(() => {
      const findColumns: MRT_ColumnDef<PasFindRow>[] = [
        {
          id: 'photo',
          header: intl.formatMessage(tableMessages.photo),
          size: 70,
          Cell: ({ row }) => <PasFindThumbnail find={row.original.find} />,
        },
        {
          id: 'id',
          header: intl.formatMessage(
            stage === 'unprocessed' ? tableMessages.id : tableMessages.pasId
          ),
          size: stage === 'unprocessed' ? 100 : 160,
          Cell: ({ row }) => (
            <BaseLink
              underline="none"
              component={Link}
              to={`/f/${row.original.id}/edit`}
            >
              {stage === 'unprocessed'
                ? row.original.id
                : row.original.find.amcrId || '—'}
            </BaseLink>
          ),
        },
        {
          accessorKey: 'title',
          header: intl.formatMessage(tableMessages.find),
          size: 320,
          Cell: ({ row }) => <PasFindTitle row={row.original} />,
        },
        {
          accessorKey: 'situation',
          header: intl.formatMessage(tableMessages.situation),
          size: 220,
        },
      ];

      if (showUnprocessedStatus) {
        findColumns.push({
          id: 'status',
          header: intl.formatMessage(tableMessages.status),
          size: 100,
          muiTableHeadCellProps: rightAlignedCellProps,
          muiTableBodyCellProps: rightAlignedCellProps,
          Cell: ({ row }) => (
            <PasFindStatusCell
              find={row.original.find}
              preview={previewsById.get(row.original.id)}
              onEdit={onEdit}
            />
          ),
        });
      } else if (showPhotoStatus) {
        findColumns.push({
          id: 'status',
          header: intl.formatMessage(tableMessages.status),
          size: 100,
          muiTableHeadCellProps: rightAlignedCellProps,
          muiTableBodyCellProps: rightAlignedCellProps,
          Cell: ({ row }) => <PasPhotoStatusCell find={row.original.find} />,
        });
      }

      if (showCreateAction) {
        findColumns.push({
          id: 'actions',
          header: intl.formatMessage(tableMessages.actions),
          size: 100,
          Cell: ({ row }) => (
            <PasCreateButton findId={row.original.id} onCreate={onCreate} />
          ),
        });
      } else if (showPhotoActions) {
        findColumns.push({
          id: 'actions',
          header: intl.formatMessage(tableMessages.actions),
          size: 200,
          Cell: ({ row }) => (
            <PasPhotoActions
              find={row.original.find}
              canSubmit={stage === 'withPhoto'}
              onAddPhotos={onAddPhotos}
              onSubmit={onSubmit}
            />
          ),
        });
      }

      return findColumns;
    }, [
      intl,
      onAddPhotos,
      onCreate,
      onEdit,
      onSubmit,
      previewsById,
      showCreateAction,
      showPhotoActions,
      showPhotoStatus,
      showUnprocessedStatus,
      stage,
    ]);

    const handlePaginationChange = useCallback(
      (updater: MRT_Updater<MRT_PaginationState>) => {
        const next =
          typeof updater === 'function' ? updater(pagination) : updater;

        setPage(next.pageIndex + 1);
      },
      [pagination, setPage]
    );

    const countValues = useMemo(() => ({ count: filteredRows.length }), [
      filteredRows.length,
    ]);

    const renderTopToolbar = useCallback(
      ({ table }: { table: MRT_TableInstance<PasFindRow> }) => (
        <Box sx={toolbarStyles}>
          <SearchField table={table} />
          <FindListFilters
            state={state}
            enableSiteFilter={false}
            presetOmittedKeys={SITE_PRESET_OMITTED_KEYS}
          />
          <Typography variant="body2" sx={countStyles}>
            <FormattedMessage
              id="finds.count"
              defaultMessage="{count, plural, one {# find} other {# finds}}"
              values={countValues}
            />
          </Typography>
        </Box>
      ),
      [countValues, state]
    );

    const table = useMaterialReactTable({
      columns,
      data: filteredRows,
      localization,
      manualFiltering: true,
      enableSorting: false,
      enableToolbarInternalActions: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      initialState,
      state: { isLoading: loading, globalFilter: search, pagination },
      autoResetPageIndex: false,
      onGlobalFilterChange: setSearch,
      onPaginationChange: handlePaginationChange,
      muiPaginationProps: paginationProps,
      muiSearchTextFieldProps: searchTextFieldProps,
      muiTablePaperProps: tablePaperProps,
      muiTableBodyCellProps: tableBodyCellProps,
      renderTopToolbar,
    });

    return <MaterialReactTable table={table} />;
  }
);

PasFindTable.displayName = 'PasFindTable';

export { PasFindTable };
